import { useMemo } from 'react';
import { Copy, Lock } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { matrixFromPermissions } from '@/pages/company/roles/rolesLogic';
import { useRoleDetail } from '@/pages/company/roles/hooks/useRolesQueries';
import { PermissionMatrix, ScopeLegend } from './PermissionMatrix';

interface SystemRoleDrawerProps {
  roleId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** «Дублировать как кастомную» → передаёт загруженный RoleDetail наверх. */
  onDuplicate: (roleId: string) => void;
}

/**
 * SystemRoleDrawer — просмотр системной роли (фрейм 4, AK-343).
 *
 * Матрица read-only (права из кода бэка, §1.5). Редактировать нельзя —
 * единственный путь правки это «Дублировать как кастомную». Для ролей без
 * write-прав в матрице (lawyer) бэк отдаёт `system_note` + нулевую матрицу —
 * показываем как «только просмотр», НЕ «сломанная».
 */
export function SystemRoleDrawer({
  roleId,
  open,
  onOpenChange,
  onDuplicate,
}: SystemRoleDrawerProps) {
  const detailQ = useRoleDetail(open ? roleId : null);
  const detail = detailQ.data;

  const matrix = useMemo(
    () => (detail ? matrixFromPermissions(detail.permissions) : null),
    [detail],
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-[640px]">
        <SheetTitle className="flex items-center gap-2">
          {detail?.name ?? 'Системная роль'}
          <Badge variant="secondary" className="gap-1">
            <Lock className="h-3 w-3" /> Системная
          </Badge>
        </SheetTitle>
        <SheetDescription>
          Стандартная роль системы. Права заданы в коде и одинаковы для всех хозяйств.
        </SheetDescription>

        {detailQ.isLoading || !matrix ? (
          <div className="mt-6 space-y-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="mt-6 space-y-5">
            <Alert>
              <Lock className="h-4 w-4" />
              <AlertDescription>
                Системную роль нельзя менять. Чтобы сделать свою версию —{' '}
                <b>дублируйте</b> её и правьте копию.
              </AlertDescription>
            </Alert>

            {detail?.system_note && (
              <Alert>
                <AlertDescription>{detail.system_note}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Права по разделам · только просмотр
                </span>
                <ScopeLegend />
              </div>
              <PermissionMatrix matrix={matrix} onChange={null} />
            </div>
          </div>
        )}

        <div className="mt-6 flex gap-3 border-t border-border pt-4">
          <Button variant="ghost" className="flex-1" onClick={() => onOpenChange(false)}>
            Закрыть
          </Button>
          <Button
            className="flex-1"
            disabled={!detail}
            onClick={() => detail && onDuplicate(detail.id)}
          >
            <Copy className="h-4 w-4" />
            Дублировать как кастомную
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
